<script lang="ts" setup>
import { computed } from 'vue'
import * as fb from '@ironsight/form-builder'

import ArrayFieldSettings from '@/components/form-builder/field-settings/ArrayFieldSettings.vue'
import GenericSelectorSettings from '@/components/form-builder/field-settings/GenericSelectorSettings.vue'
import NumberFieldSettings from '@/components/form-builder/field-settings/NumberFieldSettings.vue'
import EnumStringFieldSettings from '@/components/form-builder/field-settings/EnumStringFieldSettings.vue'
import BooleanFieldSettings from '@/components/form-builder/field-settings/BooleanFieldSettings.vue'
import MarkdownFieldSettings from '@/components/form-builder/field-settings/MarkdownFieldSettings.vue'
import DateTimeFieldSettings from '@/components/form-builder/field-settings/DateTimeFieldSettings.vue'
import LookupFieldSettings from '@/components/form-builder/field-settings/LookupFieldSettings.vue'
import TextEntryFieldSettings from '@/components/form-builder/field-settings/TextEntryFieldSettings.vue'
import ObjectFieldSettings from '@/components/form-builder/field-settings/ObjectFieldSettings.vue'
import FormButtonsFieldSettings from '@/components/form-builder/field-settings/FormButtonsFieldSettings.vue'
import ReferenceFieldSettings from '@/components/form-builder/field-settings/ReferenceFieldSettings.vue'
import type { FormFieldDefinition } from '@ironsight/form-builder/dist/form_fields/form_field_definition'

interface Props {
  field: FormFieldDefinition
}

const props = defineProps<Props>()

type ModifyFieldPayload = {
  propertyKey: string
  fieldValue: unknown
  key: string
}

const emit = defineEmits<{
  (e: 'modify-field', payload: ModifyFieldPayload): void
  (e: 'remove-field', field: FormFieldDefinition): void
}>()

const isReferenceField = computed(() => {
  return props.field instanceof fb.ReferenceField
})

const fieldComponentType = computed(() => {
  if (props.field instanceof fb.ReferenceField) {
    return ReferenceFieldSettings
  }
  if (props.field instanceof fb.ObjectFieldDefinition) {
    return ObjectFieldSettings
  }
  if (
    props.field instanceof fb.SubjectLocationFieldDefinition ||
    props.field instanceof fb.SubjectFieldDefinition
  ) {
    return null
  }
  if (props.field instanceof fb.CreateableSelectorDefinition) {
    return EnumStringFieldSettings
  }
  if (props.field instanceof fb.DateFieldDefinition) {
    return DateTimeFieldSettings
  }
  if (props.field instanceof fb.LookupFieldDefinition) {
    return LookupFieldSettings
  }
  if (props.field instanceof fb.GenericSelectorDefinition) {
    return GenericSelectorSettings
  }
  if (props.field instanceof fb.NumberFieldDefinition) {
    return NumberFieldSettings
  }
  if (props.field instanceof fb.EnumStringFieldDefinition) {
    return EnumStringFieldSettings
  }
  if (props.field instanceof fb.FormButtonsDefinition) {
    return FormButtonsFieldSettings
  }
  if (props.field instanceof fb.BooleanFieldDefinition) {
    return BooleanFieldSettings
  }
  if (props.field instanceof fb.MarkdownFieldDefinition) {
    return MarkdownFieldSettings
  }
  if (props.field instanceof fb.ArrayFieldDefinition) {
    return ArrayFieldSettings
  }
  if (props.field instanceof fb.TextEntryFieldDefinition) {
    return TextEntryFieldSettings
  }
  return null
})

function modifyField(payload: ModifyFieldPayload) {
  emit('modify-field', payload)
}

function removeField(field: FormFieldDefinition) {
  emit('remove-field', field)
}
</script>

<template>
  <vs-wrapper>
    <vs-heading
      v-show="!isReferenceField && fieldComponentType"
      class="textColor-grey"
      type="overline"
    >
      Specific Field Settings
    </vs-heading>
    <component
      :is="fieldComponentType"
      :key="field.uniqueKey()"
      :field="field"
      @modify-field="modifyField"
    />
  </vs-wrapper>
</template>
